import { useEffect, useState } from 'react';
import { RefreshCw, Briefcase } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import EmptyState from '../../../components/common/EmptyState';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammFundService } from '../services/pammService';
import { formatCurrency, formatDate, exportToCSV } from '../utils';
import type { DataTableColumn } from '../../../components/ui/data-table';

export interface PammListItem {
  id: string;
  pammName: string;
  managerName: string;
  mt5Id: string;
  minimumInvestment: number;
  maximumInvestment: number;
  investmentStartDate: string;
  investmentEndDate: string;
  tradingStartDate: string;
  tradingEndDate: string;
  pammCreatedDate: string;
}

export default function InvestmentRequests() {
  const [pammList, setPammList] = useState<PammListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const funds = await pammFundService.getAll();
      const listData: PammListItem[] = funds.map((f, idx) => ({
        id: f.id,
        pammName: f.name,
        managerName: f.managerName || 'Manager',
        mt5Id: `MT5-PAMM-88${idx + 10}1`,
        minimumInvestment: f.minimumInvestment || 1000,
        maximumInvestment: f.maximumInvestment || 100000,
        investmentStartDate: '2024-01-01',
        investmentEndDate: '2024-12-31',
        tradingStartDate: '2024-01-15',
        tradingEndDate: '2024-12-25',
        pammCreatedDate: f.createdAt ? formatDate(f.createdAt) : '2024-01-01',
      }));

      const fallbackItems: PammListItem[] = [
        {
          id: 'PAMM-001',
          pammName: 'Gold Scalper Master',
          managerName: 'James Richardson',
          mt5Id: 'MT5-882019',
          minimumInvestment: 1000,
          maximumInvestment: 50000,
          investmentStartDate: '2024-01-01',
          investmentEndDate: '2024-12-31',
          tradingStartDate: '2024-01-05',
          tradingEndDate: '2024-12-25',
          pammCreatedDate: '2023-12-15',
        },
        {
          id: 'PAMM-002',
          pammName: 'Forex Alpha Trend',
          managerName: 'Sarah Mitchell',
          mt5Id: 'MT5-882020',
          minimumInvestment: 2500,
          maximumInvestment: 100000,
          investmentStartDate: '2024-02-01',
          investmentEndDate: '2024-11-30',
          tradingStartDate: '2024-02-10',
          tradingEndDate: '2024-11-20',
          pammCreatedDate: '2024-01-10',
        },
        {
          id: 'PAMM-003',
          pammName: 'Conservative Multi-Asset',
          managerName: 'Alexander Thompson',
          mt5Id: 'MT5-882021',
          minimumInvestment: 500,
          maximumInvestment: 25000,
          investmentStartDate: '2024-01-15',
          investmentEndDate: '2024-12-15',
          tradingStartDate: '2024-01-20',
          tradingEndDate: '2024-12-10',
          pammCreatedDate: '2024-01-05',
        },
        {
          id: 'PAMM-004',
          pammName: 'Crypto & FX Hybrid',
          managerName: 'Maria Gonzalez',
          mt5Id: 'MT5-882022',
          minimumInvestment: 3000,
          maximumInvestment: 150000,
          investmentStartDate: '2024-03-01',
          investmentEndDate: '2024-10-31',
          tradingStartDate: '2024-03-05',
          tradingEndDate: '2024-10-25',
          pammCreatedDate: '2024-02-18',
        },
        {
          id: 'PAMM-005',
          pammName: 'High Yield Arbitrage',
          managerName: 'David Chen',
          mt5Id: 'MT5-882023',
          minimumInvestment: 5000,
          maximumInvestment: 250000,
          investmentStartDate: '2024-01-10',
          investmentEndDate: '2024-12-20',
          tradingStartDate: '2024-01-15',
          tradingEndDate: '2024-12-15',
          pammCreatedDate: '2024-01-02',
        },
      ];

      fallbackItems.forEach(item => {
        if (!listData.some(d => d.id === item.id || d.mt5Id === item.mt5Id)) {
          listData.push(item);
        }
      });

      setPammList(listData);
    } catch {
      setError('Failed to load PAMM list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const columns: DataTableColumn<PammListItem>[] = [
    {
      id: 'managerName',
      header: 'Manager Name',
      accessor: row => (
        <div>
          <p className="font-medium text-xs text-foreground">
            {row.managerName}
          </p>
          <p className="text-[11px] text-muted-foreground">{row.pammName}</p>
        </div>
      ),
      sortable: true,
      sortValue: row => row.managerName,
      width: '160px',
    },
    {
      id: 'mt5Id',
      header: 'MT5 ID',
      accessor: row => (
        <span className="font-mono text-xs font-semibold ">{row.mt5Id}</span>
      ),
      sortable: true,
      sortValue: row => row.mt5Id,
      width: '120px',
    },
    {
      id: 'minimumInvestment',
      header: 'Minimum Investment',
      accessor: row => (
        <span className="text-xs font-medium text-foreground">
          {formatCurrency(row.minimumInvestment)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.minimumInvestment,
      width: '150px',
    },
    {
      id: 'maximumInvestment',
      header: 'Maximum Investment',
      accessor: row => (
        <span className="text-xs font-medium text-foreground">
          {formatCurrency(row.maximumInvestment)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.maximumInvestment,
      width: '150px',
    },
    {
      id: 'investmentStartDate',
      header: 'Investment Start Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.investmentStartDate}
        </span>
      ),
      sortable: true,
      sortValue: row => row.investmentStartDate,
      width: '160px',
    },
    {
      id: 'investmentEndDate',
      header: 'Investment End Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.investmentEndDate}
        </span>
      ),
      sortable: true,
      sortValue: row => row.investmentEndDate,
      width: '160px',
    },
    {
      id: 'tradingStartDate',
      header: 'Trading Start Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.tradingStartDate}
        </span>
      ),
      sortable: true,
      sortValue: row => row.tradingStartDate,
      width: '150px',
    },
    {
      id: 'tradingEndDate',
      header: 'Trading End Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.tradingEndDate}
        </span>
      ),
      sortable: true,
      sortValue: row => row.tradingEndDate,
      width: '150px',
    },
    {
      id: 'pammCreatedDate',
      header: 'PAMM Created Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.pammCreatedDate}
        </span>
      ),
      sortable: true,
      sortValue: row => row.pammCreatedDate,
      width: '150px',
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="PAMM List"
          description="Overview of available PAMM accounts and trading programs"
        />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="PAMM List"
          description="Overview of available PAMM accounts and trading programs"
        />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="PAMM List"
        description="Overview of available PAMM accounts and trading programs"
      >
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </PageHeader>

      {pammList.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No PAMM Accounts Found"
          description="PAMM accounts will appear here once created."
        />
      ) : (
        <DataTable
          columns={columns}
          data={pammList}
          searchValue={searchValue}
          showColumnVisibility={true}
          searchPlaceholder="Search PAMM list..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(pammList, 'pamm_list')}
        />
      )}
    </div>
  );
}
